import {
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { platformTransitionStyle } from '../../../../../shared/helpers'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../../shared/providers/KeyDownProvider'
import type { KeyTypeEnum as LegacyKeyTypeEnum } from '../../../../../shared/hooks/useKeyDownHandler'
import { useNavigationMode } from '../../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import tts from '../../../lib/TTS'
import { CaptionsButton } from '../../CaptionsButton/CaptionsButton'
import { useVideoPlayerData } from '../VideoPlayerContext'

import ProgressBar from './ProgressBar/ProgressBar'

import dayjs from 'dayjs'
import { useHybridFocusable } from '../../../hooks/useHybridFocusable'
import ForbiddenActionMessage from '../../ForbiddenActionMessage'
import type { PlayerControlsProps } from './types'

declare const COMPILING_FOR_TIZEN: boolean

const CONTROL_AUTOHIDE_DELAY_MS = 5000

function PlayerControls(props: PlayerControlsProps) {
  const {
    playerStateRef,
    setLastUserInteraction,
    setIsUserTryingToSeek,
    setPlayerStatus,
    setIsUpNextVisible,
    setShowControls,
    playerState,
  } = useVideoPlayerData()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { isCursorMode } = useNavigationMode()
  const controlsSuppressed = props.controlsSuppressed ?? false
  const showControlsState = playerState.showControls
  const showControls = showControlsState && !controlsSuppressed
  const areCaptionsEnabled = useAppSelector((state) => state.captions.enabled)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pointerHoverRef = useRef(false)

  const updateShowControls = useCallback(
    (nextVisible: boolean) => {
      if (playerStateRef.current.showControls !== nextVisible) {
        setShowControls(nextVisible)
      }
    },
    [setShowControls]
  )

  const isPointerOverControls = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Node)) {
      return false
    }

    if (target instanceof Element) {
      const ccButton = target.closest('.ccButton')
      if (ccButton) {
        return true
      }

      const trickPlay = target.closest('.trick-play-container')
      if (trickPlay) {
        return true
      }
    }

    return false
  }, [])

  const adInteraction = ['adPlaying', 'adPaused'].includes(
    playerStateRef.current.status
  )
  const keysToListen = [
    KeyTypeEnum.Right,
    KeyTypeEnum.Left,
    KeyTypeEnum.FastForward,
    KeyTypeEnum.Rewind,
  ]

  const forbiddenMessageKeys = keysToListen as unknown as LegacyKeyTypeEnum[]

  const { focusKey } = useHybridFocusable({
    preferredChildFocusKey: 'progress-bar',
  })

  const toggleCaptions = useCallback(() => {
    dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: !areCaptionsEnabled },
    })
    tts.addTextToRead(`Captions ${areCaptionsEnabled ? 'Off' : 'On'}`)
  }, [areCaptionsEnabled])

  const goBackToVideoDetails = useCallback(() => {
    const windowUrl = window.location.href.split('/')[4]
    navigate(`/video-details/${windowUrl}`, {
      replace: true,
      state: {
        ...location.state,
        fromVOD: true,
      },
    })
  }, [navigate])

  useKeyDown(
    [
      KeyTypeEnum.Right,
      KeyTypeEnum.Left,
      KeyTypeEnum.Up,
      KeyTypeEnum.Down,
      KeyTypeEnum.Enter,
      KeyTypeEnum.Back,
      KeyTypeEnum.FastForward,
      KeyTypeEnum.Rewind,
      KeyTypeEnum.Stop,
      KeyTypeEnum.Pause,
      KeyTypeEnum.Play,
    ],
    (key) => {
      setLastUserInteraction(dayjs())
      if (!playerStateRef.current.upNextData?.isUpNextVisible) {
        if (key === KeyTypeEnum.Back) {
          if (showControls) {
            if (playerStateRef.current.isUserTryingToSeek) {
              setIsUserTryingToSeek(false)
              props.play()
              setPlayerStatus('playing')
            }
            pointerHoverRef.current = false
            if (timerRef.current) {
              clearTimeout(timerRef.current)
              timerRef.current = null
            }
            updateShowControls(false)
          } else {
            goBackToVideoDetails()
          }
        } else if (key === KeyTypeEnum.Stop) {
          goBackToVideoDetails()
        } else if (key === KeyTypeEnum.Enter) {
          if (isCursorMode && showControls) {
            props.playOrPause()
            resetTimer()
          } else if (!showControlsState && !controlsSuppressed) {
            updateShowControls(true)
            setFocus('progress-bar')
            resetTimer()
          } else {
            resetTimer()
          }
        } else if (!showControlsState && !controlsSuppressed) {
          updateShowControls(true)
          setFocus('progress-bar')
          resetTimer()
        } else {
          resetTimer()
        }
      } else {
        if (key === KeyTypeEnum.Back) {
          setIsUpNextVisible(false)
        }
      }
    }
  )

  useEffect(() => {
    if (playerState.upNextData?.isUpNextVisible) {
      updateShowControls(false)
      pointerHoverRef.current = false
    }
  }, [playerState.upNextData?.isUpNextVisible, updateShowControls])

  useEffect(() => {
    if (controlsSuppressed && playerStateRef.current.showControls) {
      updateShowControls(false)
    }
    if (controlsSuppressed) {
      pointerHoverRef.current = false
    }
  }, [controlsSuppressed, updateShowControls])

  useEffect(() => {
    if (isCursorMode && !showControls) {
      setShowControls(true)
    }
  }, [isCursorMode])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    if (controlsSuppressed) {
      updateShowControls(false)
      return
    }

    if (!isCursorMode) {
      timerRef.current = setTimeout(() => {
        if (!playerStateRef.current.isUserTryingToSeek) {
          setShowControls(false)
        } else {
          resetTimer()
        }
      }, CONTROL_AUTOHIDE_DELAY_MS)
    } else {
      timerRef.current = setTimeout(() => {
        if (playerStateRef.current.isUserTryingToSeek) {
          resetTimer()
          return
        }
        if (pointerHoverRef.current) {
          resetTimer()
          return
        }
        updateShowControls(false)
      }, CONTROL_AUTOHIDE_DELAY_MS)
    }
  }, [
    controlsSuppressed,
    updateShowControls,
    isCursorMode,
    playerStateRef,
    setShowControls,
  ])

  const handlePointerEnterControls = useCallback(() => {
    if (!isCursorMode || controlsSuppressed) {
      return
    }
    pointerHoverRef.current = true
    setLastUserInteraction(dayjs())
    resetTimer()
  }, [
    isCursorMode,
    controlsSuppressed,
    setLastUserInteraction,
    updateShowControls,
    resetTimer,
  ])

  const handlePointerLeaveControls = useCallback(() => {
    pointerHoverRef.current = false
    if (!controlsSuppressed) {
      resetTimer()
    }
  }, [controlsSuppressed, resetTimer])

  const handleWheel = useCallback(() => {
    if (!isCursorMode || controlsSuppressed) return

    setLastUserInteraction(dayjs())
    updateShowControls(true)
    resetTimer()
  }, [
    isCursorMode,
    controlsSuppressed,
    setLastUserInteraction,
    updateShowControls,
    resetTimer,
  ])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isCursorMode || controlsSuppressed) return

      if (!isPointerOverControls(event.target)) {
        setLastUserInteraction(dayjs())
        props.playOrPause()
      }
      updateShowControls(true)
      resetTimer()
    },
    [
      isCursorMode,
      controlsSuppressed,
      setLastUserInteraction,
      updateShowControls,
      resetTimer,
      props.playOrPause,
      isPointerOverControls,
    ]
  )

  useEffect(() => {
    if (!isCursorMode) {
      pointerHoverRef.current = false
    }
  }, [isCursorMode])

  return (
    <FocusContext.Provider value={focusKey}>
      {adInteraction && (
        <ForbiddenActionMessage keysToListen={forbiddenMessageKeys} />
      )}
      <div
        id="video-player-underlay"
        style={{
          transition: platformTransitionStyle('opacity 500ms linear'),
          opacity: showControls ? 1 : 0,
        }}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
      >
        <div
          id="video-player-ui"
          onPointerEnter={handlePointerEnterControls}
          onPointerLeave={handlePointerLeaveControls}
        >
          {props.captionsAvailable && !COMPILING_FOR_TIZEN && (
            <CaptionsButton
              isVisible={showControls}
              active={areCaptionsEnabled}
              onEnter={toggleCaptions}
            />
          )}
          <ProgressBar
            playOrPause={props.playOrPause}
            seek={props.seek}
            pause={props.pause}
            play={props.play}
            setIsSeekingViaPointer={props.setIsSeekingViaPointer}
          />
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default PlayerControls
